import { useState, type ReactNode } from 'react';
import type { TooltipProps } from 'antd/es/tooltip';
import { FormattedMessage } from 'react-intl';

import { logger } from '@/config/logger';
import { env } from '@/env';
import { NotificationId } from '@/modules/api/codegen';
import { useNotification } from '@/modules/notifications/hooks';
import { Button, Heading, Text, Tooltip } from '@/modules/ui';

import { UserGuide } from '../UserGuide';
import * as felaRules from './UserGuideSecretsView.rules';

export interface UserGuideSecretsViewProps {
    show?: boolean;
    placement: TooltipProps['placement'];
    align?: TooltipProps['align'];
    children: ReactNode;
}

/**
 * Admin onboarding guide.
 */
export const UserGuideSecretsView = ({ show = true, children, placement, align }: UserGuideSecretsViewProps) => {
    const { notification, isLoading, update } = useNotification(NotificationId.adminOnboarding);
    const [visible, setVisible] = useState(true);

    if (env.VITE_PLAYWRIGHT_RUN) {
        logger.warn('UserGuideSecretsView not rendered in playwright');
        return null;
    }

    const open = show && !isLoading && !notification?.dismissedAt && visible;

    return (
        <UserGuide
            open={open}
            extend={{
                item: felaRules.guideItem,
            }}
        >
            <Tooltip
                open={open}
                title={
                    <div data-testid='user-guide-secrets-view'>
                        <Heading elementType='h2' color='black' customStyle={felaRules.title}>
                            <FormattedMessage id='user-guide.secrets.view.title' />
                        </Heading>
                        <Text elementType='p' variant='ParagraphSmallMedium' color='brownGrey' customStyle={felaRules.text}>
                            <FormattedMessage id='user-guide.secrets.view.text' />
                        </Text>
                        <Button
                            type='primary'
                            onClick={() => {
                                setVisible(false);
                                update.rejectNotification();
                            }}
                            customStyle={felaRules.button}
                            data-testid='user-guide-close-button'
                        >
                            <FormattedMessage id='user-guide.secrets.view.button' />
                        </Button>
                    </div>
                }
                placement={placement}
                align={align}
                customOverlayStyle={felaRules.tooltip}
            >
                {children}
            </Tooltip>
        </UserGuide>
    );
};
